import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import * as L from 'leaflet';
import { MapPosition } from "../../models/map-position";
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HelpService } from '/app/services/help/help.service';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css'],
  standalone: true, 
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule] 
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  private map: any;
  private markers: L.Marker[] = [];
  
  @Input() height: string = '300px';
  @Input() center: any = [51.678418, 7.809007];
  @Input() zoom: number = 12;
  @Input() maxZoom: number = 19;
  @Input() minZoom: number = 12;
  @Input() positions: MapPosition[] = [];
  @Input() singlePosition: MapPosition | null = null; // Nueva propiedad para marcador único

  constructor(private helpService: HelpService) { }

  showHelp(): void {
    this.helpService.startTour(this.helpService.getMapHelpSteps());
  }

  ngAfterViewInit() {
    // Inicializar el mapa con un retraso de 500ms
    setTimeout(() => {
      if (!this.map) {
        this.initMap();
      }
    }, 500);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar cambios en positions
    if (changes['positions'] && !changes['positions'].firstChange) {
      this.updateMap();
    }
    
    // Detectar cambios en singlePosition
    if (changes['singlePosition'] && !changes['singlePosition'].firstChange) {
      this.updateMap();
    }
    
    // Detectar cambios en center y ajustar el mapa
    if (changes['center'] && !changes['center'].firstChange && this.map) {
      this.map.setView([this.center[0], this.center[1]], this.zoom);
    }
  }

  ngOnDestroy() {
    // Destruir el mapa cuando el componente se destruya
    if (this.map) {
      this.map.remove();
    }
  }

  private initMap() {
    // Asegúrate de que el mapa solo se inicializa una vez
    if (this.map) {
      return;
    }

    // Inicializamos el mapa
    this.map = L.map('map', {
      center: [this.center[0], this.center[1]],
      zoom: this.zoom
    });

    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    });

    this.addMarkers();  // Añadir los marcadores
    tiles.addTo(this.map);
  }

  private updateMap() {
    // Eliminar todos los marcadores existentes
    this.clearMarkers();
    
    // Agregar los nuevos marcadores
    this.addMarkers();
  }
  
  private clearMarkers() {
    // Eliminar todos los marcadores del mapa
    this.markers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.markers = [];
  }

  private getColorWithOpacity(points: number) {
    const opacity = 1;
  
    if (isNaN(points)) {
      return `rgba(128, 128, 128, ${opacity})`; // gris
    } else if (points >= 100) {
      return `rgba(231, 76, 60, ${opacity})`; // rojo
    } else if (points >= 80) {
      return `rgba(251, 140, 0, ${opacity})`; // naranja
    } else if (points >= 60) {
      return `rgba(199, 199, 19, ${opacity})`; // amarillo oscuro
    } else {
      return `rgba(74, 200, 127, ${opacity})`; // verde
    }
  }

  private addMarkers() {
    // Si se proporciona singlePosition, usarlo como única posición
    if (this.singlePosition) {
      this.addMarker(this.singlePosition);
    } 
    // Si no, usar el array de positions
    else if (this.positions && this.positions.length > 0) {
      this.positions.forEach((position: MapPosition) => {
        this.addMarker(position);
      });
    }
  }
  
  private addMarker(position: MapPosition) {
    const colorWithOpacity = this.getColorWithOpacity(position.points);

    const iconHtml = `
      <svg width="40px" height="40px" viewBox="-3 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <g id="Page-1" stroke="black" stroke-width="0.5" fill="none" fill-rule="evenodd">
          <g id="Dribbble-Light-Preview" transform="translate(-183.000000, -5399.000000)" fill="${colorWithOpacity}">
            <g id="icons" transform="translate(56.000000, 160.000000)">
              <path d="M137,5246.635 C137,5244.978 135.657,5243.635 134,5243.635 C132.343,5243.635 131,5244.978 131,5246.635 C131,5248.292 132.343,5249.635 134,5249.635 C135.657,5249.635 137,5248.292 137,5246.635 M141,5246 C141,5249.866 134,5259 134,5259 C134,5259 127,5249.866 127,5246 C127,5242.134 130.134,5239 134,5239 C137.866,5239 141,5242.134 141,5246 M135,5246.635 C135,5247.186 134.551,5247.635 134,5247.635 C133.449,5247.635 133,5247.186 133,5246.635 C133,5246.084 133.449,5245.635 134,5245.635 C134.551,5245.635 135,5246.084 135,5246.635" id="pin_fill_sharp_circle-[#635]">
              </path>
            </g>
          </g>
        </g>
      </svg>
    `;

    const customIcon = L.divIcon({
      html: iconHtml,
      iconSize: [30, 50],
      iconAnchor: [15, 50],
      className: '' // Elimina cualquier estilo predeterminado
    });

    const marker = L.marker([position.latitude, position.longitude], { icon: customIcon });

    const popupContent = `
      <div>
        <h2>${position.name}</h2>
        <p>${position.device}</p>
        <p>${position.description}</p>
        <p>${position.risklevel}</p>
        <p>${position.longitude}, ${position.latitude}</p>
      </div>
    `;

    const popup = L.popup().setContent(popupContent);

    marker.bindPopup(popup);
    marker.addTo(this.map);
    this.markers.push(marker);
  }
}